import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';

const navItemsZh = [
  { label: '首頁', path: '/' },
  { label: '關於我們', path: '/about' },
  { label: '核心技術', path: '/technology' },
  { label: '產品與材料', path: '/products' },
  { label: '應用場景與解決方案', path: '/solutions' },
  { label: '合作模式與經銷夥伴', path: '/partnership' },
  { label: '永續與碳效益', path: '/sustainability' },
  { label: '聯絡我們', path: '/contact' },
];

const navItemsEn = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'Technology', path: '/technology' },
  { label: 'Products', path: '/products' },
  { label: 'Solutions', path: '/solutions' },
  { label: 'Partnership', path: '/partnership' },
  { label: 'Sustainability', path: '/sustainability' },
  { label: 'Contact', path: '/contact' },
];

const navItemsJa = [
  { label: 'ホーム', path: '/' },
  { label: '会社概要', path: '/about' },
  { label: 'コア技術', path: '/technology' },
  { label: '製品・材料', path: '/products' },
  { label: '応用シーンとソリューション', path: '/solutions' },
  { label: 'パートナーシップ・販売代理', path: '/partnership' },
  { label: 'サステナビリティと炭素効果', path: '/sustainability' },
  { label: 'お問い合わせ', path: '/contact' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { lang, setLang } = useLanguage();

  const navItems = lang === 'en' ? navItemsEn : lang === 'ja' ? navItemsJa : navItemsZh;

  const ctaLabel = lang === 'en' ? 'Contact Us' : lang === 'ja' ? 'お問い合わせ' : '聯絡我們';
  const ctaLabelMobile = lang === 'en' ? 'Contact Us Now' : lang === 'ja' ? 'お問い合わせはこちら' : '立即聯絡我們';
  const openMenuLabel = lang === 'en' ? 'Open menu' : lang === 'ja' ? 'メニューを開く' : '開啟選單';
  const closeMenuLabel = lang === 'en' ? 'Close menu' : lang === 'ja' ? 'メニューを閉じる' : '關閉選單';

  const logoMain = lang === 'en' ? 'PolyQ Applied Materials' : lang === 'ja' ? '聚量応用材料' : '聚量應用材料';
  const logoSub = lang === 'en' ? 'PolyQ Applied Materials Co., Ltd.' : lang === 'ja' ? '聚量応用材料株式会社' : 'JuuLiang Applied Materials';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-header border-b border-neutral-border'
          : 'bg-white/90 backdrop-blur-sm border-b border-neutral-border/60'
      }`}
    >
      <div className="max-w-screen-xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-[68px]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 flex-shrink-0 cursor-pointer">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-gradient-to-br from-brand-green to-brand-blue flex-shrink-0">
              <i className="ri-leaf-line text-white text-lg"></i>
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-[0.9375rem] font-bold text-neutral-dark tracking-tight whitespace-nowrap">{logoMain}</span>
              <span className="text-[0.6875rem] text-neutral-mid tracking-wide whitespace-nowrap">{logoSub}</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden xl:flex items-center gap-0">
            {navItems.slice(0, -1).map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-2 py-2 text-[0.875rem] font-medium rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer relative group ${
                  isActive(item.path) ? 'text-brand-green' : 'text-neutral-dark hover:text-brand-green'
                }`}
              >
                {item.label}
                <span className={`absolute bottom-0 left-2 right-2 h-0.5 bg-brand-green rounded-full transition-all duration-200 ${isActive(item.path) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`} />
              </Link>
            ))}
          </nav>

          {/* Desktop right: Lang switcher + CTA */}
          <div className="hidden xl:flex items-center gap-1.5 flex-shrink-0">
            <div className="flex items-center rounded-full border border-neutral-border bg-white overflow-hidden">
              <button
                onClick={() => setLang('zh')}
                className={`px-2.5 py-1.5 text-[0.75rem] font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${lang === 'zh' ? 'bg-brand-green text-white' : 'text-neutral-mid hover:text-neutral-dark'}`}
              >
                中
              </button>
              <button
                onClick={() => setLang('en')}
                className={`px-2.5 py-1.5 text-[0.75rem] font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${lang === 'en' ? 'bg-brand-green text-white' : 'text-neutral-mid hover:text-neutral-dark'}`}
              >
                EN
              </button>
              <button
                onClick={() => setLang('ja')}
                className={`px-2.5 py-1.5 text-[0.75rem] font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${lang === 'ja' ? 'bg-brand-green text-white' : 'text-neutral-mid hover:text-neutral-dark'}`}
              >
                日
              </button>
            </div>
            <Link
              to="/contact"
              className={`px-4 py-2 rounded-full text-[0.9375rem] font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${isActive('/contact') ? 'bg-brand-green-dark text-white' : 'bg-brand-green text-white hover:bg-brand-green-dark'}`}
            >
              {ctaLabel}
            </Link>
          </div>

          {/* Mobile right: lang + hamburger */}
          <div className="xl:hidden flex items-center gap-2">
            <div className="flex items-center rounded-full border border-neutral-border bg-white overflow-hidden">
              <button onClick={() => setLang('zh')} className={`px-2 py-1 text-[0.75rem] font-semibold transition-all duration-200 cursor-pointer ${lang === 'zh' ? 'bg-brand-green text-white' : 'text-neutral-mid'}`}>中</button>
              <button onClick={() => setLang('en')} className={`px-2 py-1 text-[0.75rem] font-semibold transition-all duration-200 cursor-pointer ${lang === 'en' ? 'bg-brand-green text-white' : 'text-neutral-mid'}`}>EN</button>
              <button onClick={() => setLang('ja')} className={`px-2 py-1 text-[0.75rem] font-semibold transition-all duration-200 cursor-pointer ${lang === 'ja' ? 'bg-brand-green text-white' : 'text-neutral-mid'}`}>日</button>
            </div>
            <button
              className="w-10 h-10 flex items-center justify-center rounded-md text-neutral-dark hover:text-brand-green hover:bg-neutral-light transition-colors cursor-pointer"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? closeMenuLabel : openMenuLabel}
            >
              <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      <div className={`xl:hidden transition-all duration-300 overflow-hidden ${menuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="bg-white border-t border-neutral-border shadow-[0_8px_24px_rgba(0,0,0,0.10)]">
          <nav className="flex flex-col py-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center px-6 h-[52px] text-[1.125rem] font-semibold transition-colors cursor-pointer ${isActive(item.path) ? 'text-brand-green bg-brand-green-light' : 'text-neutral-dark hover:text-brand-green hover:bg-neutral-light'}`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="px-6 py-4 border-t border-neutral-border">
            <Link to="/contact" className="block w-full text-center py-3 rounded-full bg-brand-green text-white text-[1.125rem] font-semibold hover:bg-brand-green-dark transition-colors cursor-pointer whitespace-nowrap">
              {ctaLabelMobile}
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}
